import { SIMPLE_RESERVATION_EMAIL_LANGUAGE, COMPONENT_VALUE_TYPES as types } from 'shared/constants';
import type { PropsSchemaList } from 'shared/types/PropsSchema';

interface FormOptions {
  required: boolean;
  label: string;
}

export interface Props {
  editor?: object;
  header: {
    title: string;
    description: string;
  };
  form: {
    firstName: FormOptions;
    lastName: FormOptions;
    phone: {
      required: boolean;
      label: string;
    };
    email: FormOptions;
    state: {
      required: boolean;
      label: string;
      customLabel: string;
      list: {
        text: string;
        value: string;
      }[];
    };
    fieldLabelColor: string;
  };
  footer: {
    buttonText: string;
    disclaimerText: string;
  };
  successMessage: {
    header: string;
    body: string;
  };
  emailSettings: {
    emailLanguage: string;
  };
}

const propsSchema: PropsSchemaList = [
  {
    key: 'header',
    label: 'Header',
    type: types.OBJECT,
    children: [
      {
        key: 'title',
        label: 'Title',
        type: types.TINYMCE,
        options: {
          menubar: false,
          toolbar: 'fontsizeselect fontWeight lineheight forecolor',
          height: 150,
        }
      },
      {
        key: 'description',
        label: 'Description',
        type: types.TINYMCE,
        options: {
          menubar: false,
          toolbar: 'fontsizeselect fontWeight italic underline link lineheight forecolor',
          height: 200,
        }
      },
    ],
  },
  {
    key: 'form',
    label: 'Simple FX Reservation Form',
    type: types.OBJECT,
    children: [
      {
        key: 'firstName',
        label: 'First Name',
        type: types.OBJECT,
        children: [
          {
            key: 'required',
            label: 'Required Field',
            type: types.BOOLEAN,
          },
          {
            key: 'label',
            label: 'First Name Text',
            type: types.TEXT,
          },
        ]
      },
      {
        key: 'lastName',
        label: 'Last Name',
        type: types.OBJECT,
        children: [
          {
            key: 'required',
            label: 'Required Field',
            type: types.BOOLEAN,
          },
          {
            key: 'label',
            label: 'Last Name Text',
            type: types.TEXT,
          },
        ]
      },
      {
        key: 'phone',
        label: 'Phone',
        type: types.OBJECT,
        children: [
          {
            key: 'required',
            label: 'Required Field',
            type: types.BOOLEAN,
          },
          {
            key: 'label',
            label: 'Phone Text',
            type: types.TEXT,
          },
        ]
      },
      {
        key: 'email',
        label: 'Email',
        type: types.OBJECT,
        children: [
          {
            key: 'required',
            label: 'Required Field',
            type: types.BOOLEAN,
          },
          {
            key: 'label',
            label: 'Email Text',
            type: types.TEXT,
          },
        ]
      },
      {
        key: 'state',
        label: 'Emirate/State',
        type: types.OBJECT,
        children: [
          {
            key: 'required',
            label: 'Required Field',
            type: types.BOOLEAN,
          },
          {
            key: 'label',
            label: 'Emirate/State Dropdown Text',
            type: types.TEXT,
          },
          {
            key: 'customLabel',
            label: 'Custom Input Field Text',
            type: types.TEXT,
          },
          {
            key: 'list',
            label: 'List of Emirate/States',
            type: types.ARRAY,
            children: [
              {
                key: 'text',
                label: 'Emirate/State Name (This is what the user sees)',
                type: types.TEXT,
              },
              {
                key: 'value',
                label: 'Emirate/State Value (This value is stored in the backend)',
                type: types.TEXT,
              },
            ]
          },
        ]
      },
      {
        key: 'fieldLabelColor',
        label: 'Form Field Label Color',
        type: types.COLOR_PICKER,
      },
    ],
  },
  {
    key: 'footer',
    label: 'Footer',
    type: types.OBJECT,
    children: [
      {
        key: 'buttonText',
        label: 'Submit Button Text',
        type: types.TEXT,
      },
      {
        key: 'disclaimerText',
        label: 'Disclaimer Text',
        type: types.TINYMCE,
        options: {
          menubar: false,
          toolbar: 'fontsizeselect fontWeight italic underline link lineheight forecolor',
          height: 200,
        },
      },
    ],
  },
  {
    key: 'successMessage',
    label: 'Success Message',
    type: types.OBJECT,
    children: [
      {
        key: 'header',
        label: 'Header',
        type: types.TEXT,
      },
      {
        key: 'body',
        label: 'Body',
        type: types.TEXT,
      },
    ],
  },
  {
    key: 'emailSettings',
    label: 'Email Settings',
    type: types.OBJECT,
    children: [
      {
        key: 'emailLanguage',
        label: "Pick what language the customer's email will be in",
        type: types.ENUM,
        options: SIMPLE_RESERVATION_EMAIL_LANGUAGE,
      },
    ],
  },
];

const defaultProps: Props = {
  header: {
    title: 'Free Reservation',
    description: 'Reserve today and be among the first to experience FX SUPERONE as we will soon launch it.',
  },
  form: {
    firstName: {
      required: true,
      label: 'First Name',
    },
    lastName: {
      required: true,
      label: 'Last Name',
    },
    phone: {
      required: true,
      label: 'Phone Number',
    },
    email: {
      required: true,
      label: 'Email',
    },
    state: {
      required: true,
      label: 'Emirate',
      customLabel: 'Custom Location',
      list: [
        { text: 'Abu Dhabi Emirate', value: 'Abu Dhabi Emirate' },
        { text: 'Dubai Emirate', value: 'Dubai Emirate' },
        { text: 'Sharjah Emirate', value: 'Sharjah Emirate' },
        { text: 'Ajman Emirate', value: 'Ajman Emirate' },
        { text: 'Umm Al Quwain Emirate', value: 'Umm Al Quwain Emirate' },
        { text: 'Ras Al Khaimah Emirate', value: 'Ras Al Khaimah Emirate' },
        { text: 'Fujairah Emirate', value: 'Fujairah Emirate' },
        { text: 'Custom', value: 'Custom' },
      ]
    },
    fieldLabelColor: 'rgba(92, 94, 98, 1)',
  },
  footer: {
    buttonText: 'Reservation',
    disclaimerText: 'By clicking "Submit," I consent to FF contacting me regarding this request using the provided contact information. I acknowledge that calls or texts may be made using automatic or computer-assisted dialing, or pre-recorded messages. Standard message and data rates apply.<br><br>'
  },
  successMessage: {
    header: 'Reservation Successful',
    body: 'Thank you very much for your reservation. We will prioritize your purchase when it is issued.',
  },
  emailSettings: {
    emailLanguage: 'EN',
  },
};

export default {
  title: 'Simple FX Reservation',
  id: 'SimpleFXReservation',
  propsSchema,
  defaultProps,
}